import { keepPreviousData } from '@tanstack/react-query';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { SharePlanVestingType } from '../../types/pool-plans.types';
import { useAppQuery } from '../use-app-query.hook';

const VESTING_GRAPH_REFETCH_INTERVAL_MS = 60 * 60 * 1000;

export enum VestingGraphPointGranularity {
  MILESTONE = 'milestone',
  MONTHLY = 'monthly',
}

export enum VestingGraphPointStatus {
  EXPIRED = 'expired',
  UNVESTED = 'unvested',
  VESTED = 'vested',
}

export type VestingGraphPoint = {
  cumulativePercent: number;
  cumulativeShares: number;
  date: string;
  milestoneName?: string;
  monthsToComplete: number;
  periodShares: number;
  status: VestingGraphPointStatus;
};

export type VestingGraphCliff = {
  cumulativePercentAtCliff: number;
  endDate: string;
  months: number;
  startDate: string;
};

export type VestingGraphVestingPeriod = {
  endDate: string;
  index: number;
  planAllocation?: number;
  startDate: string;
};

export type VestingGraphData = {
  accelerationDate?: string;
  asOfDate: string;
  cliff?: VestingGraphCliff;
  expiryDate?: string;
  fullVestingDate: null | string;
  grantId: string;
  granularity: VestingGraphPointGranularity;
  originalSchedulePoints?: VestingGraphPoint[];
  planName: string;
  points: VestingGraphPoint[];
  startDate: string;
  vestingPeriods?: VestingGraphVestingPeriod[];
  vestingTaskId: string;
  vestingType: SharePlanVestingType;
};

export const useVestingGraph = ({
  companyId,
  enabled = true,
  stakeholderId,
  vestingTaskId,
}: {
  companyId: string;
  enabled?: boolean;
  stakeholderId: string;
  vestingTaskId: string;
}) => {
  const {
    data: vestingGraph,
    isError,
    isFetching,
    isLoading,
    isPlaceholderData,
  } = useAppQuery<null | VestingGraphData>({
    enabled: !!companyId && !!stakeholderId && !!vestingTaskId && enabled,
    placeholderData: keepPreviousData,
    queryFn: () =>
      ApiService.get({
        endpoint: BackendRoute.COMPANIES,
        routePath: [
          companyId,
          'stakeholders',
          stakeholderId,
          'vesting-tasks',
          vestingTaskId,
          'vesting-graph',
        ],
      }),
    queryKey: [QueryKey.GET_VESTING_GRAPH, companyId, stakeholderId, vestingTaskId],
    refetchInterval: VESTING_GRAPH_REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });

  return {
    isError,
    isFetching,
    isLoading,
    isPlaceholderData,
    vestingGraph,
  };
};
